/** Pinia store for editor UI state — panels, dialogs, flags. */
import { defineStore } from 'pinia';
import { CANVAS_VIEWS } from '@/constants/canvasView';
import type { CanvasView } from '@/types/ui/canvasView';
import { DEFAULT_GRID_ZOOM } from '@/constants/attributeGrid';
import { useElementEditStore } from '@/stores/elementEditStore';
import { useEditorStore } from '@/stores/editorStore';
import { backingScaleForZoom } from '@/helpers/canvasResolution';
import type { LayerHighlight, UiState } from '@/types/stores/ui';

export const useUiStore = defineStore('ui', {
  state: (): UiState => ({
    variantGridOpen: false,
    sidebarVisible: true,
    appNavOpen: false,
    showSaveDialog: false,
    showUnsavedChangesDialog: false,
    hasSavedInSession: false,
    showSaveComponentDialog: false,
    saveComponentLayerIds: [],
    canvasView: CANVAS_VIEWS.MOSAIC,
    desiredIsCore: null,
    activePanelKey: null,
    effectsMenuOpen: false,
    showExportDialog: false,
    showExportSummaryDialog: false,
    timelineOpen: false,
    timelineZoom: 50,
    variantGridZoom: DEFAULT_GRID_ZOOM,
    selectedBlockId: null,
    highlight: null,
    expandedTrackIds: [],
    animationHiddenIds: [],
    isQuickExporting: false,
    isCanvasViewportMoving: false,
    canvasBackingScale: 1,
    liveTextEdits: {},
    canvasRefitToken: 0,
    translationErrors: {}
  }),
  actions: {
    /** Ask the mosaic viewport to fit-to-screen on the next tick. */
    requestCanvasRefit() {
      this.canvasRefitToken += 1;
    },
    setLiveText(layerId: string, text: string) {
      this.liveTextEdits[layerId] = text;
    },
    clearLiveText(layerId: string) {
      delete this.liveTextEdits[layerId];
    },
    setTranslationError(
      variantId: string,
      attributeId: string,
      message: string
    ) {
      const row = this.translationErrors[variantId] ?? {};
      row[attributeId] = message;
      this.translationErrors[variantId] = row;
    },
    clearTranslationError(variantId: string, attributeId: string) {
      const row = this.translationErrors[variantId];
      if (!row) return;
      delete row[attributeId];
      if (Object.keys(row).length === 0)
        delete this.translationErrors[variantId];
    },
    clearTranslationErrors() {
      this.translationErrors = {};
    },
    /**
     * Opens the save-as-element dialog for `layerIds`. Ignored on an empty
     * selection — there would be nothing to save.
     */
    openSaveComponentDialog(layerIds: string[]) {
      if (layerIds.length === 0) return;
      if (useElementEditStore().active) return;
      this.saveComponentLayerIds = [...layerIds];
      this.showSaveComponentDialog = true;
    },
    toggleVariantGrid() {
      this.variantGridOpen = !this.variantGridOpen;
    },
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    toggleAppNav() {
      this.appNavOpen = !this.appNavOpen;
    },
    setCanvasView(view: CanvasView) {
      this.canvasView = view;
    },
    openPanel(key: string) {
      this.sidebarVisible = true;
      this.activePanelKey = key;
      this.effectsMenuOpen = false;
    },
    closePanel() {
      this.activePanelKey = null;
    },
    togglePanel(key: string) {
      if (this.activePanelKey === key) {
        this.closePanel();
        return;
      }
      this.openPanel(key);
    },
    toggleEffectsMenu() {
      this.effectsMenuOpen = !this.effectsMenuOpen;
      if (this.effectsMenuOpen) {
        this.closePanel();
      }
    },
    closeEffectsMenu() {
      this.effectsMenuOpen = false;
    },
    toggleTimeline() {
      this.timelineOpen = !this.timelineOpen;
    },
    setSelectedBlock(id: string | null) {
      this.selectedBlockId = id;
    },
    /** Use `highlightLayers` / `clearHighlight` in selectionService. */
    setHighlight(highlight: LayerHighlight | null) {
      if (!highlight && !this.highlight) return;
      this.highlight = highlight;
    },
    setTrackExpanded(layerId: string, open: boolean) {
      const isOpen = this.expandedTrackIds.includes(layerId);
      if (open === isOpen) return;
      this.expandedTrackIds = open
        ? [...this.expandedTrackIds, layerId]
        : this.expandedTrackIds.filter(id => id !== layerId);
    },
    setAnimationHidden(layerId: string, hidden: boolean) {
      const isHidden = this.animationHiddenIds.includes(layerId);
      if (hidden === isHidden) return;
      this.animationHiddenIds = hidden
        ? [...this.animationHiddenIds, layerId]
        : this.animationHiddenIds.filter(id => id !== layerId);
    },
    resetUi() {
      this.variantGridOpen = false;
      this.showSaveDialog = false;
      this.showUnsavedChangesDialog = false;
      this.hasSavedInSession = false;
      this.showSaveComponentDialog = false;
      this.saveComponentLayerIds = [];
      this.canvasView = CANVAS_VIEWS.MOSAIC;
      this.desiredIsCore = null;
      this.activePanelKey = null;
      this.effectsMenuOpen = false;
      this.timelineOpen = false;
      this.timelineZoom = 50;
      this.variantGridZoom = DEFAULT_GRID_ZOOM;
      this.selectedBlockId = null;
      this.highlight = null;
      this.expandedTrackIds = [];
      this.animationHiddenIds = [];
      this.isQuickExporting = false;
      this.isCanvasViewportMoving = false;
      this.canvasBackingScale = backingScaleForZoom(
        useEditorStore().viewports.mosaic.zoom,
        null
      );
      this.translationErrors = {};
      this.showExportDialog = false;
      this.showExportSummaryDialog = false;
    }
  }
});
